import { useState, useEffect, useRef, useCallback } from "react";
import L from "leaflet";
import Layout from "../components/Layout";
import api from "../services/api";
import { fetchEta, formatEta, formatDistance } from "../utils/eta";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const vehicleEmoji = (type) => {
  const map = { motorcycle: "🏍️", bicycle: "🚲", van: "🚐", truck: "🚛", bus: "🚌", lorry: "🚚" };
  return map[type] || "🚗";
};

// Staleness thresholds (rider pings every 30s, so anything beyond that means a gap)
const STALE_AFTER_SECONDS = 90; // 3 missed pings — show amber warning
const OFFLINE_AFTER_SECONDS = 300; // 5 minutes — show red "offline" state

const getStaleness = (recordedAt) => {
  if (!recordedAt) return "unknown";
  const seconds = (Date.now() - new Date(recordedAt).getTime()) / 1000;
  if (seconds < STALE_AFTER_SECONDS) return "live";
  if (seconds < OFFLINE_AFTER_SECONDS) return "stale";
  return "offline";
};

const timeAgo = (recordedAt) => {
  if (!recordedAt) return "never";
  const seconds = Math.floor((Date.now() - new Date(recordedAt).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
};

const getRiderAvailability = (riderId, orders) => {
  const activeOrder = orders.find(
    (o) =>
      o.assigned_rider_id === riderId &&
      ["assigned", "picked", "in_transit"].includes(o.status)
  );
  return activeOrder ? "on_delivery" : "available";
};

export default function MapPage() {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef({});

  const [riders, setRiders] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [selectedRider, setSelectedRider] = useState(null);
  const [error, setError] = useState("");
  const [riderLocations, setRiderLocations] = useState({});
  const [, setTick] = useState(0);
  const [riderEtas, setRiderEtas] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [ridersRes, ordersRes] = await Promise.all([
        api.get("/riders"),
        api.get("/orders"),
      ]);
      setRiders(ridersRes.data || []);
      setOrders(ordersRes.data?.data || ordersRes.data || []);
      setError("");
    } catch {
      setError("Failed to load data. Retrying...");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mapInstance.current || !mapRef.current) return;

    const map = L.map(mapRef.current, {
      center: [1.2921, 36.8219],
      zoom: 12,
      zoomControl: true,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    mapInstance.current = map;
    setMapReady(true);

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapReady) return;
    fetchData();
  }, [mapReady, fetchData]);

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 10000);
    return () => clearInterval(t);
  }, []);

  const markerIcon = (rider, staleness) => {
    const borderColor = staleness === "live" ? "white" : staleness === "stale" ? "#F59E0B" : "#94A3B8";
    const bgColor = staleness === "offline" ? "#94A3B8" : "#f97316";
    const opacity = staleness === "offline" ? 0.6 : 1;

    return L.divIcon({
      html: `
        <div style="
          background: ${bgColor};
          border: 3px solid ${borderColor};
          border-radius: 50%;
          width: 40px; height: 40px;
          display: flex; align-items: center;
          justify-content: center; font-size: 18px;
          box-shadow: 0 2px 10px rgba(249,115,22,0.5);
          cursor: pointer;
          opacity: ${opacity};
          position: relative;
        ">${vehicleEmoji(rider.vehicle_type)}
        ${staleness === "stale" ? `<div style="position:absolute;top:-2px;right:-2px;width:12px;height:12px;background:#F59E0B;border:2px solid white;border-radius:50%;"></div>` : ""}
        ${staleness === "offline" ? `<div style="position:absolute;top:-2px;right:-2px;width:12px;height:12px;background:#94A3B8;border:2px solid white;border-radius:50%;"></div>` : ""}
        </div>
      `,
      className: "",
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    });
  };

  const popupHtml = (rider, staleness, recordedAt, availability) => {
    const statusLabel = {
      live: `<span style="color:#22c55e;font-weight:600;">● Live</span>`,
      stale: `<span style="color:#F59E0B;font-weight:600;">● Signal weak</span>`,
      offline: `<span style="color:#94A3B8;font-weight:600;">● Offline</span>`,
      unknown: `<span style="color:#94A3B8;font-weight:600;">● No data</span>`,
    }[staleness];

    const availabilityBadge =
      availability === "available"
        ? `<span style="background:#DCFCE7;color:#15803D;font-size:11px;font-weight:700;padding:2px 8px;border-radius:99px;">Available</span>`
        : `<span style="background:#FFF7ED;color:#C2410C;font-size:11px;font-weight:700;padding:2px 8px;border-radius:99px;">On delivery</span>`;

    return `
      <div style="font-family: DM Sans, sans-serif; min-width: 170px; padding: 4px;">
        <p style="font-weight:700; margin:0 0 4px; font-size:14px;">${rider.name}</p>
        <p style="color:#64748b; font-size:12px; margin:0 0 6px; text-transform:capitalize;">
          ${vehicleEmoji(rider.vehicle_type)} ${rider.vehicle_type}
        </p>
        <div style="margin-bottom:6px;">${availabilityBadge}</div>
        <div style="font-size:12px; margin-bottom:2px;">${statusLabel}</div>
        <p style="color:#94a3b8; font-size:11px; margin:0;">Last seen: ${timeAgo(recordedAt)}</p>
      </div>
    `;
  };

  const refreshLocations = useCallback(async () => {
    if (!mapInstance.current || riders.length === 0) return;

    const activeRiders = riders.filter((r) => r.is_active);
    const locUpdates = {};
    const etaUpdates = {};

    for (const rider of activeRiders) {
      try {
        const res = await api.get(`/rider/${rider.id}/location`);
        const { latitude, longitude, recorded_at } = res.data || {};
        if (!latitude || !longitude) continue;

        locUpdates[rider.id] = { latitude, longitude, recorded_at };
        const staleness = getStaleness(recorded_at);
        const icon = markerIcon(rider, staleness);
        const availability = getRiderAvailability(rider.id, orders);

        if (markersRef.current[rider.id]) {
          markersRef.current[rider.id].setLatLng([latitude, longitude]);
          markersRef.current[rider.id].setIcon(icon);
          markersRef.current[rider.id].setPopupContent(
            popupHtml(rider, staleness, recorded_at, availability)
          );
          markersRef.current[rider.id].getPopup()?.update();
        } else {
          const marker = L.marker([latitude, longitude], { icon })
            .addTo(mapInstance.current)
            .bindPopup(popupHtml(rider, staleness, recorded_at, availability));
          markersRef.current[rider.id] = marker;
        }

        const activeOrder = orders.find(
          (o) =>
            o.assigned_rider_id === rider.id &&
            ["assigned", "picked", "in_transit"].includes(o.status) &&
            o.delivery_lat &&
            o.delivery_lng
        );

        if (activeOrder && staleness !== "offline") {
          const eta = await fetchEta(
            latitude,
            longitude,
            activeOrder.delivery_lat,
            activeOrder.delivery_lng
          );
          etaUpdates[rider.id] = eta ? { ...eta, customerName: activeOrder.customer_name } : null;
        } else {
          etaUpdates[rider.id] = null;
        }
      } catch {
        // Location not available for this rider — skip silently
      }
    }

    setRiderLocations((prev) => ({ ...prev, ...locUpdates }));
    setRiderEtas((prev) => ({ ...prev, ...etaUpdates }));
    setLastUpdate(new Date());
  }, [riders, orders]);

  useEffect(() => {
    if (!mapReady || riders.length === 0) return;
    refreshLocations();
    const interval = setInterval(refreshLocations, 15000);
    return () => clearInterval(interval);
  }, [mapReady, riders, refreshLocations]);

  const focusRider = async (rider) => {
    setSelectedRider(rider);
    try {
      const res = await api.get(`/rider/${rider.id}/location`);
      const { latitude, longitude } = res.data || {};
      if (latitude && longitude && mapInstance.current) {
        mapInstance.current.flyTo([latitude, longitude], 15, { duration: 1.5 });
        markersRef.current[rider.id]?.openPopup();
      }
    } catch {}
  };

  const activeRiders = riders.filter((r) => r.is_active);
  const inTransit = orders.filter((o) => o.status === "in_transit").length;

  return (
    <Layout>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');
        .leaflet-container { z-index: 1; }
      `}</style>

      <div className="flex flex-col" style={{ height: "calc(100vh - 64px)", fontFamily: "'DM Sans', sans-serif" }}>
        <div className="px-6 py-4 border-b border-gray-100 bg-white shrink-0">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Sora', sans-serif" }}>
                Live Map
              </h1>
              <p className="text-gray-500 text-sm mt-0.5">
                {loading ? "Loading drivers..." : `${activeRiders.length} active · ${inTransit} in transit`}
                {lastUpdate && (
                  <span className="ml-2 text-green-500 text-xs">
                    · Updated{" "}
                    {lastUpdate.toLocaleTimeString("en-KE", {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    })}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-green-50 text-green-600 border border-green-100">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                Auto-refresh 15s
              </div>
              <button
                onClick={() => {
                  fetchData();
                  refreshLocations();
                }}
                disabled={loading}
                className="inline-flex items-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium px-3 py-2 rounded-xl transition-colors disabled:opacity-50"
              >
                <svg className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
                Refresh
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-2 text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
              ⚠️ {error}
            </div>
          )}
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="w-64 shrink-0 border-r border-gray-100 bg-white flex flex-col overflow-hidden hidden md:flex">
            <div className="px-4 py-3 border-b border-gray-50">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
                Active Drivers ({activeRiders.length})
              </p>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex flex-col gap-2 p-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-14 bg-gray-100 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : activeRiders.length === 0 ? (
                <div className="text-center py-12 px-4 text-gray-400">
                  <p className="text-3xl mb-2">🏍️</p>
                  <p className="text-sm font-medium">No active drivers</p>
                  <p className="text-xs mt-1">Drivers appear here when online.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {activeRiders.map((rider) => {
                    const loc = riderLocations[rider.id];
                    const staleness = loc ? getStaleness(loc.recorded_at) : "unknown";
                    const dotColor = {
                      live: "bg-green-500",
                      stale: "bg-amber-500",
                      offline: "bg-slate-400",
                      unknown: "bg-slate-300",
                    }[staleness];
                    const availability = getRiderAvailability(rider.id, orders);

                    return (
                      <button
                        key={rider.id}
                        onClick={() => focusRider(rider)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-orange-50 transition-colors ${
                          selectedRider?.id === rider.id ? "bg-orange-50 border-r-2 border-orange-500" : ""
                        }`}
                      >
                        <div className="relative shrink-0">
                          <div className="w-9 h-9 bg-orange-100 rounded-xl flex items-center justify-center text-base">
                            {vehicleEmoji(rider.vehicle_type)}
                          </div>
                          <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${dotColor}`} />
                        </div>
                        <div className="overflow-hidden flex-1">
                          <div className="flex items-center gap-1.5">
                            <p className="font-semibold text-gray-900 text-sm truncate">{rider.name}</p>
                            <span
                              className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${
                                availability === "available"
                                  ? "bg-green-50 text-green-700"
                                  : "bg-orange-50 text-orange-700"
                              }`}
                            >
                              {availability === "available" ? "Available" : "On delivery"}
                            </span>
                          </div>
                          <p className="text-gray-400 text-xs">
                            {staleness === "stale" && <span className="text-amber-600 font-medium">Signal weak · </span>}
                            {staleness === "offline" && <span className="text-slate-500 font-medium">Offline · </span>}
                            {loc ? timeAgo(loc.recorded_at) : "No data"}
                          </p>
                        </div>
                        <svg className="w-3.5 h-3.5 text-gray-300 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                        </svg>
                      </button>
                    );
                  })}
                </div>
              )}

              {inTransit > 0 && (
                <div className="p-4 border-t border-gray-100 mt-2">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                    In Transit ({inTransit})
                  </p>
                  {orders
                    .filter((o) => o.status === "in_transit")
                    .map((o) => (
                      <div key={o.id} className="bg-orange-50 border border-orange-100 rounded-xl p-3 mb-2">
                        <p className="font-semibold text-gray-900 text-xs">{o.customer_name}</p>
                        <p className="text-gray-500 text-xs mt-0.5 truncate">{o.address}</p>
                        <p className="text-orange-500 font-mono text-xs mt-1">{o.tracking_token}</p>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 relative flex flex-col">
            {(() => {
              const enRoute = activeRiders
                .map((r) => ({ rider: r, eta: riderEtas[r.id] }))
                .filter((x) => x.eta);

              if (enRoute.length === 0) return null;

              return (
                <div className="bg-white border-b border-gray-100 px-4 py-2.5 flex items-center gap-3 overflow-x-auto shrink-0 z-10">
                  {enRoute.map(({ rider, eta }) => (
                    <div
                      key={rider.id}
                      className="flex items-center gap-2.5 bg-orange-50 border border-orange-100 rounded-xl px-3 py-2 shrink-0 cursor-pointer hover:bg-orange-100 transition-colors"
                      onClick={() => focusRider(rider)}
                    >
                      <span className="text-base">{vehicleEmoji(rider.vehicle_type)}</span>
                      <div className="leading-tight">
                        <p className="text-xs font-bold text-gray-900">
                          {rider.name} <span className="text-gray-400 font-normal">→</span> {eta.customerName}
                        </p>
                        <p className="text-xs text-orange-600 font-semibold">
                          ETA {formatEta(eta.seconds)}{" "}
                          <span className="text-gray-400 font-normal">· {formatDistance(eta.meters)}</span>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {!mapReady && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-gray-50 gap-3">
                <svg className="w-8 h-8 animate-spin text-orange-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                <p className="text-gray-500 text-sm font-medium">Initializing map...</p>
              </div>
            )}

            {mapReady && !loading && activeRiders.length === 0 && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-white border border-gray-200 shadow-lg rounded-2xl px-5 py-3 text-sm text-gray-500 flex items-center gap-2">
                🏍️ No active drivers with GPS locations yet.
              </div>
            )}

            <div ref={mapRef} className="w-full h-full" />
          </div>
        </div>
      </div>
    </Layout>
  );
}